import React, { useState, useMemo } from "react";
import { Box, IconButton, Typography, Grid } from "@material-ui/core";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import { withStyles } from "@material-ui/styles";
import { compose } from "recompose";
import ApplicationUtil from "../../../utils/ApplicationUtil";

const WINDOW_SIZE = 6;

const getColorForValue = (value) => {
  const roundedVal = Math.round(value || 0);

  if (roundedVal <= 30) return "#DA4A4A";
  if (roundedVal <= 60) return "#FB923C";
  if (roundedVal <= 80) return "#2563EB";
  return "#22C55E";
};

function WeeklyEngagementBlocks({ classes, data }) {
  const [startIndex, setStartIndex] = useState(0);

  const chartItems = useMemo(() => {
    let rawData = [];
    setStartIndex(0);
    if (Array.isArray(data)) {
      rawData = data;
    } else if (data && Array.isArray(data.weeklyEngagementData)) {
      rawData = data.weeklyEngagementData;
    }
    // Sort by week number to ensure correct order
    return rawData.sort((a, b) => a.activityWeek - b.activityWeek);
  }, [data]);

  const visibleData = useMemo(() => {
    if (chartItems.length === 0) return [];
    return chartItems.slice(startIndex, startIndex + WINDOW_SIZE);
  }, [chartItems, startIndex]);

  const handlePrev = () => {
    setStartIndex((prev) => Math.max(0, prev - WINDOW_SIZE));
  };

  const handleNext = () => {
    if (startIndex + WINDOW_SIZE < chartItems.length) {
      setStartIndex((prev) => prev + WINDOW_SIZE);
    }
  };

  if (chartItems.length === 0) {
    return (
      <Box p={3}>
        <Typography color="textSecondary" align="center">
          No engagement data available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Box display="flex" alignItems="center" width="100%" mb={3}>
        <IconButton
          onClick={handlePrev}
          disabled={startIndex === 0}
          size="small"
          className={classes.navButton}
        >
          <ArrowBackIosIcon fontSize="inherit" />
        </IconButton>

        <Box className={classes.blocksContainer}>
          {visibleData.map((item) => (
            <Box
              key={item.activityWeek}
              className={classes.blockItem}
              style={{ backgroundColor: getColorForValue(item.totalValue) }}
            >
              <Typography className={classes.blockText}>
                {Math.round(item.totalValue || 0)}%
              </Typography>
            </Box>
          ))}
        </Box>

        <IconButton
          onClick={handleNext}
          disabled={startIndex + WINDOW_SIZE >= chartItems.length}
          size="small"
          className={classes.navButton}
        >
          <ArrowForwardIosIcon fontSize="inherit" />
        </IconButton>
      </Box>

      <Box className={classes.legendContainer}>
        {visibleData.map((item) => {
          const roundedValue = Math.round(item.totalValue || 0);
          const color = getColorForValue(roundedValue);
          return (
            <Grid
              container
              key={item.activityWeek}
              alignItems="center"
              className={classes.legendRow}
            >
              <Grid item>
                {/* Colored Dot */}
                <Box
                  className={classes.legendDot}
                  style={{ backgroundColor: color }}
                />
              </Grid>
              <Grid item xs>
                {/* Week Label */}
                <Typography variant="body2" className={classes.legendLabel}>
                  Week {item.activityWeek}
                </Typography>
              </Grid>
              <Grid item xs>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  className={classes.legendLabel}
                >
                  {ApplicationUtil.formatDate(item.weekStartDate)} -{" "}
                  {ApplicationUtil.formatDate(item.weekEndDate)}
                </Typography>
              </Grid>
              <Grid item>
                {/* Percentage Value */}
                <Typography variant="body2" style={{ fontWeight: "bold" }}>
                  {roundedValue}%
                </Typography>
              </Grid>
            </Grid>
          );
        })}
      </Box>
    </Box>
  );
}

const styles = (theme) => ({
  container: {
    width: "100%",
    padding: "20px 0",
    maxWidth: "600px", // Adjust based on your layout needs
    margin: "0 auto",
  },
  navButton: {
    fontSize: "16px", // Control icon size
    margin: "0 5px",
    backgroundColor: "#000",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#333 !important",
    },
  },
  blocksContainer: {
    display: "flex",
    flexGrow: 1,
    borderRadius: "4px",
    overflow: "hidden", // Ensures corners are rounded for the whole strip
    margin: "0 10px",
  },
  blockItem: {
    flex: 1, // Each block takes equal width
    height: "60px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    // No margins between blocks to make them contiguous
  },
  blockText: {
    color: "#fff",
    fontWeight: "bold",
    fontSize: "14px",
  },
  legendContainer: {
    padding: "0 20px", // Indent legend slightly from nav buttons
  },
  legendRow: {
    marginBottom: "8px",
  },
  legendDot: {
    width: "12px",
    height: "12px",
    borderRadius: "50%",
    marginRight: "12px",
  },
  legendLabel: {
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
});

export default compose(withStyles(styles, { withTheme: true }))(
  WeeklyEngagementBlocks,
);
